import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, X, CreditCard, Package, Truck, Home, CheckCircle2, XCircle, Clock, Printer, UserPlus } from "lucide-react";
import AppShell from "@/components/AppShell";
import { useAuth, ROLE_THEMES } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { api, formatApiErrorDetail } from "@/lib/api";

const STAGES = [
  { key: "placed", icon: Clock },
  { key: "confirmed", icon: Package },
  { key: "in_transit", icon: Truck },
  { key: "delivered", icon: Home },
  { key: "completed", icon: CheckCircle2 },
];

function currentStageIdx(order) {
  const s = order.status;
  if (s === "cancelled") return -1;
  if (s === "paid") return 1;
  const idx = STAGES.findIndex((st) => st.key === s);
  return idx >= 0 ? idx : 0;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

export default function Orders() {
  const { user } = useAuth();
  const { t } = useI18n();
  const theme = user ? ROLE_THEMES[user.role] : ROLE_THEMES.farmer;
  const [tab, setTab] = useState("buyer");
  const [items, setItems] = useState([]);
  const [reviewOrder, setReviewOrder] = useState(null);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const load = () => api.get("/orders", { params: { role: tab } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [tab]);

  const retryPay = async (order) => {
    try {
      const { data } = await api.post("/payments/checkout", { order_id: order.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const submitReview = async () => {
    try {
      await api.post("/reviews", { order_id: reviewOrder.id, rating, text: reviewText });
      toast.success("Review submitted");
      setReviewOrder(null); setReviewText(""); setRating(5);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const updateStatus = async (order, status) => {
    try {
      await api.post(`/orders/${order.id}/status`, { status });
      toast.success(`Status updated to ${status}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setEta = async (order, days) => {
    try {
      await api.post(`/orders/${order.id}/eta`, { days });
      toast.success(days === 0 ? "Marked as same-day delivery" : `Delivery in ${days} day(s)`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const assignPartner = async (order) => {
    const name = window.prompt("Delivery partner name?", order.delivery_partner?.name || "");
    if (name === null) return;
    if (!name.trim()) return toast.error("Name is required");
    const phone = window.prompt("Delivery partner phone (10 digits)?", order.delivery_partner?.phone || "");
    if (phone === null) return;
    if ((phone || "").replace(/\D/g, "").length < 10) return toast.error("Valid 10-digit phone required");
    const vehicle_no = window.prompt("Vehicle number (optional)?", order.delivery_partner?.vehicle_no || "") || "";
    try {
      await api.post(`/orders/${order.id}/delivery-partner`, { name, phone, vehicle_no });
      toast.success(`Assigned to ${name}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const printSlip = (order) => {
    const partner = order.delivery_partner || {};
    const eta = order.delivery_eta ? new Date(order.delivery_eta).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "—";
    const created = new Date(order.created_at).toLocaleString();
    const html = `<!doctype html><html><head><title>GTF Slip · ${order.id.slice(0,8)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, Segoe UI, Roboto, sans-serif; margin: 0; padding: 24px; color: #111; }
  .card { max-width: 480px; margin: 0 auto; border: 2px dashed #111; border-radius: 12px; padding: 20px; }
  .brand { display:flex; align-items:center; gap:8px; font-weight:800; font-size:18px; margin-bottom:6px; }
  .brand .dot { height:10px; width:10px; border-radius:50%; background:#059669; }
  .muted { color:#666; font-size:11px; text-transform:uppercase; letter-spacing:2px; }
  h1 { margin:8px 0 16px; font-size: 22px; }
  .row { display:flex; justify-content:space-between; padding: 6px 0; border-bottom: 1px dotted #ccc; font-size: 14px; }
  .row:last-child { border-bottom:none; }
  .to { margin-top: 16px; padding: 12px; background:#f7f7f7; border-radius:8px; }
  .to h3 { margin:0 0 4px; font-size:12px; text-transform:uppercase; letter-spacing:2px; color:#666; }
  .to .name { font-weight:700; font-size:16px; }
  .to .addr { white-space: pre-line; font-size: 14px; margin-top:4px; }
  .barcode { text-align:center; font-family: monospace; font-size:22px; letter-spacing:2px; margin: 20px 0 4px; }
  .foot { text-align:center; font-size:11px; color:#888; margin-top: 12px; }
  @media print { @page { margin: 8mm; } body { padding: 0; } .noprint { display:none; } }
</style></head><body>
<div class="card">
  <div class="brand"><span class="dot"></span>Grow Together Farmily</div>
  <div class="muted">Delivery slip</div>
  <h1>${escapeHtml(order.listing_name)}</h1>
  <div class="row"><span>Order</span><b>#${order.id.slice(0,8).toUpperCase()}</b></div>
  <div class="row"><span>Placed</span><span>${created}</span></div>
  <div class="row"><span>Qty × Price</span><span>${order.quantity} × ₹${order.unit_price}</span></div>
  <div class="row"><span>Delivery fee</span><span>₹${order.delivery_fee || 0}</span></div>
  <div class="row"><span>Total</span><b>₹${order.total_amount}</b></div>
  <div class="row"><span>Payment</span><span>${order.payment_method}${order.payment_status === "paid" ? " · PAID" : ""}</span></div>
  <div class="row"><span>Deliver by</span><b>${eta}</b></div>
  <div class="to">
    <h3>Deliver to</h3>
    <div class="name">${escapeHtml(order.buyer_name || "")}</div>
    ${order.buyer_phone ? `<div>📞 ${escapeHtml(order.buyer_phone)}</div>` : ""}
    <div class="addr">${escapeHtml(order.delivery_address || "(no address)")}</div>
    ${order.buyer_note ? `<div style="margin-top:6px;background:#fff7cc;padding:6px 8px;border-radius:6px;font-size:12px;">📝 ${escapeHtml(order.buyer_note)}</div>` : ""}
    ${order.custom_items ? `<div style="margin-top:6px;background:#e6f7ee;padding:6px 8px;border-radius:6px;font-size:12px;">✏️ ${escapeHtml(order.custom_items)}</div>` : ""}
  </div>
  ${partner.name ? `<div class="to" style="background:#eef7ff"><h3>Delivery partner</h3><div class="name">${escapeHtml(partner.name)}</div>${partner.phone ? `<div>📞 ${escapeHtml(partner.phone)}</div>` : ""}${partner.vehicle_no ? `<div>🚚 ${escapeHtml(partner.vehicle_no)}</div>` : ""}</div>` : ""}
  <div class="barcode">*${order.id.slice(0,10).toUpperCase()}*</div>
  <div class="foot">Sold by ${escapeHtml(order.seller_name || "")} · GTF</div>
</div>
<script>window.onload = () => setTimeout(() => window.print(), 250);</script>
</body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank", "width=520,height=720");
    if (!w) { URL.revokeObjectURL(url); toast.error("Please allow popups to print the slip"); return; }
    // Free the blob URL after the window loads (or 30s max, whichever first).
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  };

  return (
    <AppShell>
      <div className="px-4 md:px-8 lg:px-12 py-8">
        <div className="text-xs uppercase tracking-[0.24em] text-gray-500">Transactions</div>
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">{t("orders.title")}</h1>

        <div className="mt-6 inline-flex bg-white border border-gray-200 rounded-full p-1">
          {[{ k: "buyer", l: t("orders.placed_by_me") }, { k: "seller", l: t("orders.received") }].map((tb) => (
            <button key={tb.k} data-testid={`orders-tab-${tb.k}`} onClick={() => setTab(tb.k)}
              className={`px-5 py-2 rounded-full text-sm font-medium ${tab === tb.k ? `${theme.bg} text-white` : "text-gray-600"}`}>{tb.l}</button>
          ))}
        </div>

        <div className="mt-8 space-y-3">
          {items.length === 0 && <div className="text-center py-16 rounded-3xl bg-white border border-gray-100 text-gray-500">No orders yet.</div>}
          {items.map((o) => {
            const stage = currentStageIdx(o);
            const isExpanded = expandedId === o.id;
            return (
              <div key={o.id} className="rounded-2xl bg-white border border-gray-100 premium-shadow overflow-hidden">
                <div className="p-5 flex items-center gap-4 flex-wrap">
                  {o.listing_image && <img src={o.listing_image} alt="" className="h-16 w-16 rounded-xl object-cover" />}
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-semibold text-gray-900">{o.listing_name}</div>
                    <div className="text-xs text-gray-500">{tab === "buyer" ? `Seller: ${o.seller_name}` : `Buyer: ${o.buyer_name}`} · {o.items?.length > 1 ? `${o.items.length} items` : `Qty ${o.quantity}`} · {o.payment_method}</div>
                    {o.items && o.items.length > 1 && (
                      <div data-testid={`order-items-${o.id}`} className="mt-2 flex flex-wrap gap-1">
                        {o.items.map((it, i) => (
                          <span key={`it-${o.id}-${i}`} className="text-[10px] bg-gray-50 border border-gray-200 rounded-full px-2 py-0.5">{it.listing_name} × {it.quantity}{it.unit ? ` ${it.unit}` : ""}</span>
                        ))}
                      </div>
                    )}
                    {o.custom_items && (
                      <div data-testid={`order-custom-items-${o.id}`} className="mt-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                        <span className="font-semibold">✏️ Buyer&apos;s list:</span> {o.custom_items}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className={`font-bold ${theme.text}`}>₹{o.total_amount}</div>
                    <div className={`text-[10px] uppercase tracking-widest mt-1 ${o.payment_status === "paid" ? "text-emerald-700" : "text-gray-500"}`}>
                      {o.payment_status === "paid" ? "PAID · " : ""}{o.status}
                    </div>
                    {o.delivery_eta && stage < 4 && (
                      <div className="text-[10px] text-gray-500 mt-1">{t("eta.title")} <b className="text-gray-800">{new Date(o.delivery_eta).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</b></div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap w-full md:w-auto">
                    <button data-testid={`track-${o.id}`} onClick={() => setExpandedId(isExpanded ? null : o.id)}
                      className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-gray-200 text-sm text-gray-700">
                      <Truck className="h-3.5 w-3.5" />{t("orders.track")}
                    </button>
                    {tab === "seller" && (
                      <button data-testid={`print-${o.id}`} onClick={() => printSlip(o)}
                        className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-gray-200 text-sm text-gray-700 hover:bg-gray-50">
                        <Printer className="h-3.5 w-3.5" />Print slip
                      </button>
                    )}
                    {tab === "buyer" && o.payment_status === "pending" && o.payment_method !== "COD" && (
                      <button data-testid={`retry-pay-${o.id}`} onClick={() => retryPay(o)}
                        className={`inline-flex items-center gap-1 px-4 py-2 rounded-full ${theme.bg} text-white text-sm`}>
                        <CreditCard className="h-3.5 w-3.5" />{t("orders.pay_now")}
                      </button>
                    )}
                    {tab === "buyer" && !o.reviewed && (o.payment_status === "paid" || o.payment_method === "COD") && (
                      <button data-testid={`review-btn-${o.id}`} onClick={() => setReviewOrder(o)}
                        className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-amber-500 text-white text-sm">
                        <Star className="h-3.5 w-3.5" />{t("orders.review")}
                      </button>
                    )}
                    {o.reviewed && <span className="text-xs text-emerald-700 font-semibold self-center">{t("orders.reviewed")} ✓</span>}
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-5 pb-6 pt-2 border-t border-gray-100 bg-gray-50/50">
                    {/* Timeline */}
                    <div className="mt-4 grid grid-cols-5 gap-2">
                      {STAGES.map((s, idx) => {
                        const done = stage >= idx;
                        const active = stage === idx;
                        const Icon = s.icon;
                        return (
                          <div key={s.key} className="flex flex-col items-center text-center">
                            <div className={`h-10 w-10 rounded-full flex items-center justify-center border-2 transition-all ${done ? theme.bg + " text-white border-transparent" : "bg-white border-gray-200 text-gray-400"} ${active ? "ring-4 ring-emerald-200" : ""}`}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <div className={`mt-2 text-[10px] uppercase tracking-widest ${done ? "text-gray-800 font-semibold" : "text-gray-400"}`}>
                              {t(`status.${s.key}`)}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Status history */}
                    {(o.status_history || []).length > 0 && (
                      <div className="mt-6 space-y-2">
                        {(o.status_history || []).slice(-4).map((h, i) => (
                          <div key={`hist-${o.id}-${i}-${h.at}`} className="text-xs text-gray-600 flex items-center gap-2">
                            <div className={`h-2 w-2 rounded-full ${theme.bg}`} />
                            <span className="font-semibold text-gray-800">{t(`status.${h.status}`) || h.status}</span>
                            {h.note && <span>· {h.note}</span>}
                            <span className="text-gray-400 ml-auto">{new Date(h.at).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Seller: buyer contact card + delivery ETA control */}
                    {tab === "seller" && (
                      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div data-testid={`buyer-contact-${o.id}`} className="rounded-2xl bg-white border border-gray-200 p-4">
                          <div className="text-[10px] uppercase tracking-widest text-gray-500">Deliver to</div>
                          <div className="mt-1 font-semibold text-gray-900">{o.buyer_name}</div>
                          {o.buyer_phone && (
                            <a href={`tel:${o.buyer_phone}`} className="mt-1 inline-flex items-center gap-1 text-sm text-emerald-700 font-medium">
                              📞 {o.buyer_phone}
                            </a>
                          )}
                          {o.delivery_address ? (
                            <div className="mt-2 text-sm text-gray-700 whitespace-pre-line">{o.delivery_address}</div>
                          ) : (
                            <div className="mt-2 text-xs text-gray-400 italic">No address provided</div>
                          )}
                          {o.buyer_note && <div className="mt-2 text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">📝 {o.buyer_note}</div>}
                          {o.custom_items && (
                            <div className="mt-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                              <div className="font-semibold mb-0.5">✏️ Buyer&apos;s custom shopping list</div>
                              <div className="whitespace-pre-line">{o.custom_items}</div>
                            </div>
                          )}
                        </div>
                        <div className="rounded-2xl bg-white border border-gray-200 p-4">
                          <div className="text-[10px] uppercase tracking-widest text-gray-500">Delivery in</div>
                          {o.delivery_eta && (
                            <div className="mt-1 text-sm font-semibold text-gray-900">
                              {new Date(o.delivery_eta).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}
                            </div>
                          )}
                          <div className="mt-3 flex gap-1.5 flex-wrap">
                            {[0, 1, 2, 3, 6, 10].map((d) => (
                              <button key={d} data-testid={`eta-${d}-${o.id}`} onClick={() => setEta(o, d)}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium border ${o.eta_days === d ? theme.bg + " text-white border-transparent" : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"}`}>
                                {d === 0 ? "Same day" : `${d}d`}
                              </button>
                            ))}
                            <button data-testid={`eta-custom-${o.id}`} onClick={() => {
                              const v = window.prompt("Delivery in how many days?", String(o.eta_days || 3));
                              const days = parseInt(v);
                              if (!isNaN(days)) setEta(o, days);
                            }} className="px-3 py-1.5 rounded-full text-xs font-medium border border-dashed border-gray-300 text-gray-600 hover:bg-gray-50">Custom</button>
                          </div>
                          {/* Delivery partner */}
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <div className="text-[10px] uppercase tracking-widest text-gray-500">Delivery partner</div>
                            {o.delivery_partner?.name ? (
                              <div className="mt-1 flex items-start justify-between gap-2">
                                <div className="text-sm">
                                  <div className="font-semibold text-gray-900">{o.delivery_partner.name}</div>
                                  <a href={`tel:${o.delivery_partner.phone}`} className="text-emerald-700">📞 {o.delivery_partner.phone}</a>
                                  {o.delivery_partner.vehicle_no && <div className="text-xs text-gray-500">🚚 {o.delivery_partner.vehicle_no}</div>}
                                </div>
                                <button data-testid={`partner-edit-${o.id}`} onClick={() => assignPartner(o)}
                                  className="text-xs px-2 py-1 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700">Edit</button>
                              </div>
                            ) : (
                              <button data-testid={`partner-assign-${o.id}`} onClick={() => assignPartner(o)}
                                className="mt-2 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100">
                                <UserPlus className="h-3.5 w-3.5" />Assign delivery partner
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Buyer: read-only delivery summary */}
                    {tab === "buyer" && (o.delivery_address || o.buyer_phone || o.delivery_partner?.name) && (
                      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                        {(o.delivery_address || o.buyer_phone) && (
                          <div className="rounded-2xl bg-white border border-gray-200 p-4">
                            <div className="text-[10px] uppercase tracking-widest text-gray-500">Delivering to</div>
                            {o.buyer_phone && <div className="mt-1 text-sm text-gray-800">📞 {o.buyer_phone}</div>}
                            {o.delivery_address && <div className="text-sm text-gray-700 mt-1 whitespace-pre-line">{o.delivery_address}</div>}
                          </div>
                        )}
                        {o.delivery_partner?.name && (
                          <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4">
                            <div className="text-[10px] uppercase tracking-widest text-emerald-700">Delivery partner</div>
                            <div className="mt-1 font-semibold text-gray-900">{o.delivery_partner.name}</div>
                            <a href={`tel:${o.delivery_partner.phone}`} className="text-sm text-emerald-700">📞 {o.delivery_partner.phone}</a>
                            {o.delivery_partner.vehicle_no && <div className="text-xs text-gray-600 mt-1">🚚 {o.delivery_partner.vehicle_no}</div>}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Seller status controls */}
                    {tab === "seller" && o.status !== "completed" && o.status !== "cancelled" && (
                      <div className="mt-6">
                        <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">{t("orders.update_status")}</div>
                        <div className="flex gap-2 flex-wrap">
                          {["confirmed", "in_transit", "delivered", "cancelled"].map((s) => (
                            <button key={s} data-testid={`status-${s}-${o.id}`} onClick={() => updateStatus(o, s)}
                              className="px-4 py-2 rounded-full text-xs bg-white border border-gray-200 hover:bg-gray-100">
                              {t(`status.${s}`)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {reviewOrder && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-3xl p-8 premium-shadow relative">
            <button onClick={() => setReviewOrder(null)} className="absolute top-4 right-4 h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center"><X className="h-4 w-4" /></button>
            <div className="text-xs uppercase tracking-widest text-gray-500">Rate & Review</div>
            <div className="text-xl font-bold mt-1">{reviewOrder.listing_name}</div>
            <div className="text-xs text-gray-500 mt-1">Seller: {reviewOrder.seller_name}</div>

            <div className="mt-6 flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((s) => (
                <button key={s} data-testid={`star-${s}`} onClick={() => setRating(s)} className="p-1">
                  <Star className={`h-9 w-9 ${s <= rating ? "fill-amber-500 text-amber-500" : "text-gray-300"}`} />
                </button>
              ))}
            </div>
            <textarea data-testid="review-text" value={reviewText} onChange={(e) => setReviewText(e.target.value)} rows={3}
              placeholder="Share your experience..." className="mt-6 w-full rounded-xl border border-gray-200 px-4 py-3" />
            <button data-testid="review-submit" onClick={submitReview} className="mt-6 w-full py-3 rounded-full bg-emerald-600 text-white font-semibold">Submit Review</button>
          </div>
        </div>
      )}
    </AppShell>
  );
}
